/**
 * Cache for DZI (Deep Zoom Image) tile sources.
 * Fetches DZI XML with auth, parses it, and returns a tile source object
 * that OpenSeadragon can use directly (no second fetch needed).
 */

import { getAuthHeaders } from '@/lib/auth-token'

interface DziTileSource {
  Image: {
    xmlns: string
    Url: string
    Format: string
    TileSize: string
    Overlap: string
    Size: { Width: string; Height: string }
  }
}

type CacheEntry = {
  promise: Promise<DziTileSource>
  tileSource?: DziTileSource
  error?: Error
}

const dziCache = new Map<string, CacheEntry>()

/**
 * Fetch, parse, and cache a DZI tile source.
 * Returns an object OpenSeadragon can use directly as a tile source,
 * avoiding a second unauthenticated fetch.
 */
export async function getCachedTileSource(url: string): Promise<DziTileSource> {
  const cached = dziCache.get(url)

  if (cached) {
    if (cached.tileSource) return cached.tileSource
    if (cached.error) throw cached.error
    return cached.promise
  }

  const promise = fetchAndParseDzi(url)
  const entry: CacheEntry = { promise }
  dziCache.set(url, entry)

  try {
    const tileSource = await promise
    entry.tileSource = tileSource
    return tileSource
  } catch (error) {
    entry.error = error instanceof Error ? error : new Error(String(error))
    throw entry.error
  }
}

async function fetchAndParseDzi(url: string): Promise<DziTileSource> {
  const response = await fetch(url, { headers: getAuthHeaders() })
  if (!response.ok) {
    throw new Error(`Failed to fetch DZI: ${response.status} ${response.statusText}`)
  }

  const xmlText = await response.text()
  const parser = new DOMParser()
  const xmlDoc = parser.parseFromString(xmlText, 'application/xml')

  const imageElement = xmlDoc.querySelector('Image')
  const sizeElement = xmlDoc.querySelector('Size')

  if (!imageElement || !sizeElement) {
    throw new Error('Invalid DZI XML: missing Image or Size element')
  }

  // Derive the tiles URL from the DZI URL: image.dzi → image_files/
  const tilesUrl = url.replace(/\.dzi$/, '_files/')

  return {
    Image: {
      xmlns: imageElement.getAttribute('xmlns') || 'http://schemas.microsoft.com/deepzoom/2008',
      Url: tilesUrl,
      Format: imageElement.getAttribute('Format') || 'jpg',
      TileSize: imageElement.getAttribute('TileSize') || '256',
      Overlap: imageElement.getAttribute('Overlap') || '1',
      Size: {
        Width: sizeElement.getAttribute('Width') || '0',
        Height: sizeElement.getAttribute('Height') || '0',
      },
    },
  }
}

export function clearDziCache(): void {
  dziCache.clear()
}

export function isDziCached(url: string): boolean {
  return dziCache.has(url)
}
